import { apiFetch } from '@/utils'
import { useEffect, useMemo, useState } from 'react'
import { useToast } from './use-toast'
import Decimal from 'decimal.js'

interface IFinalizePriceQuote {
  sellTokenAmount?: string
  buyTokenAmount?: string
}
export const useGetTokenPrice = () => {
  const [tokenPrice, setTokenPrice] = useState<IFinalizePriceQuote>()
  const [tokenPriceLoading, setTokenPriceLoading] = useState(false)

  const { toast } = useToast()

  const fetchTokenPrice = async () => {
    try {
      !tokenPriceLoading && setTokenPriceLoading(true)
      const response = await apiFetch(`/v1/tokenPrice`)
      const fetchedPrice = await response.json()

      setTokenPrice(fetchedPrice)
    } catch (error: any) {
      console.error('Error fetching token price. ', error)
      toast({
        title: error?.title || 'Error fetching token price',
        description:
          error?.details ||
          error?.message ||
          'Something went wrong fetching token price',
        variant: 'destructive'
      })
    } finally {
      setTokenPriceLoading(false)
    }
  }

  const finalizePriceQuote = async ({
    sellTokenAmount,
    buyTokenAmount
  }: IFinalizePriceQuote) => {
    try {
      if (!sellTokenAmount && !buyTokenAmount) {
        throw new Error('Missing sellTokenAmount or buyTokenAmount')
      }
      !tokenPriceLoading && setTokenPriceLoading(true)

      let queryParams = '' //amountOut ? `?amountOut=${amountOut}` : ''
      if (sellTokenAmount) {
        queryParams = `?amountIn=${sellTokenAmount}`
      }
      if (buyTokenAmount) {
        queryParams = `?amountOut=${buyTokenAmount}`
      }
      const response = await apiFetch(`/v1/tokenPrice${queryParams}`)
      const fetchedPrice = await response.json()
      setTokenPrice(fetchedPrice)
      return {
        sellTokenAmount: fetchedPrice?.sellTokenAmount,
        buyTokenAmount: fetchedPrice?.buyTokenAmount
      }
    } catch (error: any) {
      console.error('Error fetching token price. ', error)
      toast({
        title: error?.title || 'Error fetching token price',
        description:
          error?.details ||
          error?.message ||
          'Something went wrong fetching token price',
        variant: 'destructive'
      })
    } finally {
      setTokenPriceLoading(false)
    }
  }

  useEffect(() => {
    fetchTokenPrice()
  }, [])

  const computedTokenPrice = useMemo(() => {
    if (!tokenPrice?.sellTokenAmount || !tokenPrice?.buyTokenAmount) {
      return 0
    }

    const exchangeRate = new Decimal(tokenPrice?.sellTokenAmount)
      .div(tokenPrice?.buyTokenAmount)
      .toNumber()
    return exchangeRate
  }, [tokenPrice?.sellTokenAmount, tokenPrice?.buyTokenAmount])

  return {
    tokenPrice: computedTokenPrice,
    tokenPriceLoading,
    finalizePriceQuote
  }
}
